import React, { useCallback } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import { useNotification } from '@/components/notifications';
import { useStartOAuthConnect } from '@/hooks/mutations/oauth/use-start-oauth-connect';
import { useOrgStore } from '@/stores/org-store';
import { OAUTH_CONNECTED_PATH } from '@/utils/oauth-connect-signal';
import { isSafeHttpUrl } from '@/utils/url-utils';

const POPUP_FEATURES = 'width=520,height=680';

// Overrides on top of the Button primitive, which is sized for a single line
// of text: this card is two lines beside an icon tile, and its own icon sizes
// have to survive the primitive's [&_svg]:size-4. Tailwind v4 leaves buttons on
// `cursor: default`, so the pointer has to be asked for.
const CARD_CLASS =
  'my-2 h-auto w-fit cursor-pointer justify-start gap-3 whitespace-normal rounded-lg border-border px-4 py-3 text-left no-underline transition-shadow hover:shadow-md [&_svg]:size-auto';

interface IntegrationConnectCardProps {
  label: string;
  icon: React.FC<{ className?: string; size?: number }>;
  /**
   * Provider slug. When present the card mints its own authorize URL on click
   * and `href` is not needed; the URL never has to travel through agent output.
   */
  provider?: string;
  /** Pre-built authorize link, for connect prompts with no provider slug. */
  href?: string;
  /**
   * Called once consent is under way in its own window. Lets a host that is
   * itself dismissable — a toast, say — get out of the way, since the user has
   * acted on it and the rest of the flow happens elsewhere.
   */
  onConnectStarted?: () => void;
}

export const IntegrationConnectCard = ({
  label,
  icon: Icon,
  provider,
  href,
  onConnectStarted,
}: IntegrationConnectCardProps) => {
  const notification = useNotification();
  const currentOrg = useOrgStore((state) => state.currentOrg);
  const startConnect = useStartOAuthConnect();
  const orgId = currentOrg?.orgid;

  const handleConnect = useCallback(async () => {
    if (!provider || !orgId) return;

    // Opened empty and synchronously, while the click still counts as user
    // activation. Minting the URL first and opening afterwards puts the
    // window.open past an await, by which point the activation is spent and
    // the browser blocks it.
    const popup = window.open('', '_blank', POPUP_FEATURES);
    if (!popup) {
      // Never fall back to navigating this tab. Authorization ends on a
      // different page, so a full-page redirect from the workflow editor
      // discards unsaved canvas edits — the reason consent runs in a popup at
      // all. Ask for the popup instead and leave the canvas untouched.
      notification.error(
        'Your browser blocked the authorization window. Allow pop-ups for this site, then try again.',
      );
      return;
    }
    // Sever the reverse-tabnabbing vector without the `noopener` window
    // feature, which would make window.open() return null and permanently trip
    // the popup-blocked fallback above. The cost is that the completion page
    // cannot then close itself — disowning the window disqualifies it from
    // being script-closable — so it asks the user to close it instead.
    try {
      popup.opener = null;
    } catch {
      // Cross-origin popups may reject the write; the flow still proceeds.
    }

    try {
      const freshUrl = await startConnect.mutateAsync({
        provider,
        orgId,
        returnTo: OAUTH_CONNECTED_PATH,
      });
      if (!isSafeHttpUrl(freshUrl)) {
        throw new Error('Received an unsafe authorization URL');
      }
      popup.location.href = freshUrl;
      onConnectStarted?.();
    } catch (error) {
      popup?.close();
      Sentry.captureException(error, {
        extra: {
          provider,
          orgId,
          context: 'integration-connect-card-authorize',
        },
      });
      notification.error(
        error instanceof Error && error.message
          ? `Could not start authorization: ${error.message}`
          : 'Could not start authorization. Please try again.',
      );
    }
  }, [provider, orgId, startConnect, notification, onConnectStarted]);

  const isStarting = startConnect.isPending;

  const body = (
    <>
      <div className='flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted/50'>
        {isStarting ? (
          <Loader2 className='size-5 animate-spin text-muted-foreground' />
        ) : (
          <Icon className='size-5' size={20} />
        )}
      </div>
      <div className='min-w-0'>
        <span className='block text-sm font-medium'>{label}</span>
        <span className='block text-xs text-muted-foreground'>
          {isStarting ? 'Opening…' : 'Click to authorize'}
        </span>
      </div>
      <ExternalLink className='size-3.5 shrink-0 text-muted-foreground' />
    </>
  );

  if (provider) {
    return (
      <Button
        type='button'
        variant='outline'
        onClick={handleConnect}
        disabled={isStarting}
        aria-busy={isStarting}
        className={CARD_CLASS}
      >
        {body}
      </Button>
    );
  }

  return (
    <Button variant='outline' className={CARD_CLASS} asChild>
      <a href={href} target='_blank' rel='noopener noreferrer'>
        {body}
      </a>
    </Button>
  );
};
